// Refunds management — list, approve, reject, create
import { useState, useCallback, useMemo } from "react";
import { RotateCcw, Plus, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { ModuleGuard } from "@/components/admin/modules/ModuleGuard";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { KPICard } from "@/components/admin/shared/KPICard";
import { SearchInput } from "@/components/admin/shared/SearchInput";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import {
  useRefunds,
  useCreateRefund,
  useApproveRefund,
  useRejectRefund,
  usePaymentKPIs,
} from "@/hooks/admin/usePayments";
import type { Refund, RefundStatus } from "@/lib/admin-api/payment";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<RefundStatus, string> = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-blue-100 text-blue-800 border-blue-200",
  processed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
};

const STATUS_LABELS: Record<RefundStatus, string> = {
  pending: "En attente",
  approved: "Approuvé",
  processed: "Traité",
  rejected: "Rejeté",
};

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatFcfa(amount: number): string {
  return `${amount.toLocaleString("fr-SN")} FCFA`;
}

function formatDate(d: string): string {
  return new Date(d).toLocaleDateString("fr-SN");
}

// ---------------------------------------------------------------------------
// Create refund modal
// ---------------------------------------------------------------------------

interface CreateRefundModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly isSaving: boolean;
  readonly onSave: (paymentId: string, amount: number, reason: string) => Promise<void>;
}

const REFUND_REASON_CATEGORIES = [
  { value: "defective", label: "Produit défectueux" },
  { value: "wrong_item", label: "Mauvais article livré" },
  { value: "not_delivered", label: "Non livré" },
  { value: "customer_request", label: "Demande client" },
  { value: "duplicate", label: "Paiement en double" },
  { value: "other", label: "Autre" },
] as const;

function CreateRefundModal({ open, onClose, isSaving, onSave }: CreateRefundModalProps) {
  const [paymentId, setPaymentId] = useState("");
  const [amount, setAmount] = useState("");
  const [reasonCategory, setReasonCategory] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setPaymentId("");
    setAmount("");
    setReasonCategory("");
    setReason("");
    setError(null);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    if (!paymentId.trim()) {
      setError("La référence de paiement est requise.");
      return;
    }
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setError("Le montant doit être un nombre positif.");
      return;
    }
    if (!reason.trim()) {
      setError("La raison du remboursement est requise.");
      return;
    }
    setError(null);
    try {
      await onSave(paymentId.trim(), amountNum, reason.trim());
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue.");
    }
  }, [paymentId, amount, reason, onSave, handleClose]);

  return (
    <AdminModal
      open={open}
      onClose={handleClose}
      title="Nouveau remboursement"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving} className="gap-1.5">
            {isSaving && (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            )}
            Créer le remboursement
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Référence de paiement
          </label>
          <input
            type="text"
            value={paymentId}
            onChange={(e) => setPaymentId(e.target.value)}
            placeholder="PAY-XXXXXX"
            className="h-10 w-full rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Montant à rembourser (FCFA)
          </label>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0"
            min="1"
            className="h-10 w-full rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Catégorie de raison
          </label>
          <select
            value={reasonCategory}
            onChange={(e) => setReasonCategory(e.target.value)}
            className="h-10 w-full rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          >
            <option value="">Sélectionner une catégorie</option>
            {REFUND_REASON_CATEGORIES.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Détails
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Décrivez la raison du remboursement…"
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Reject reason modal
// ---------------------------------------------------------------------------

interface RejectModalProps {
  readonly open: boolean;
  readonly refund: Refund | null;
  readonly onClose: () => void;
  readonly onConfirm: (reason: string) => Promise<void>;
  readonly isRejecting: boolean;
}

function RejectModal({ open, refund, onClose, onConfirm, isRejecting }: RejectModalProps) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setReason("");
    setError(null);
    onClose();
  }, [onClose]);

  const handleConfirm = useCallback(async () => {
    if (!reason.trim()) {
      setError("Veuillez indiquer la raison du rejet.");
      return;
    }
    setError(null);
    try {
      await onConfirm(reason.trim());
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue.");
    }
  }, [reason, onConfirm, handleClose]);

  return (
    <AdminModal
      open={open}
      onClose={handleClose}
      title="Rejeter le remboursement"
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={isRejecting}>
            Annuler
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isRejecting}
            className="gap-1.5 bg-red-600 hover:bg-red-700"
          >
            {isRejecting && (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            )}
            Rejeter
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-stone-600">
          Commande{" "}
          <span className="font-mono font-semibold text-stone-900">
            {refund?.order_ref}
          </span>{" "}
          — {refund ? formatFcfa(refund.amount_fcfa) : ""}
        </p>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Raison du rejet
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Décrivez pourquoi ce remboursement est rejeté…"
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function Refunds() {
  const [statusFilter, setStatusFilter] = useState<RefundStatus | "">("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [rejectingRefund, setRejectingRefund] = useState<Refund | null>(null);

  const params = useMemo(
    () => ({ status: statusFilter || null }),
    [statusFilter],
  );

  const { data, isLoading, isError, refetch } = useRefunds(params);
  const { data: kpis } = usePaymentKPIs();
  const createRefund = useCreateRefund();
  const approveRefund = useApproveRefund();
  const rejectRefund = useRejectRefund();

  const refunds = data?.refunds ?? [];

  const pendingCount = useMemo(
    () => refunds.filter((r) => r.status === "pending").length,
    [refunds],
  );

  const totalRefunded = useMemo(
    () =>
      refunds
        .filter((r) => r.status === "processed")
        .reduce((sum, r) => sum + r.amount_fcfa, 0),
    [refunds],
  );

  const handleCreateSave = useCallback(
    async (paymentId: string, amount: number, reason: string) => {
      await createRefund.mutateAsync({
        payment_id: paymentId,
        amount_fcfa: amount,
        reason,
      });
      setIsCreateOpen(false);
    },
    [createRefund],
  );

  const handleApprove = useCallback(
    (id: string) => {
      approveRefund.mutate(id);
    },
    [approveRefund],
  );

  const handleRejectConfirm = useCallback(
    async (reason: string) => {
      if (!rejectingRefund) return;
      await rejectRefund.mutateAsync({ id: rejectingRefund.id, reason });
      setRejectingRefund(null);
    },
    [rejectingRefund, rejectRefund],
  );

  const columns: readonly ColumnDef<Refund>[] = useMemo(
    () => [
      {
        id: "order_ref",
        header: "Commande",
        cell: (r) => (
          <span className="font-mono text-xs font-medium text-stone-900">
            {r.order_ref}
          </span>
        ),
      },
      {
        id: "amount_fcfa",
        header: "Montant",
        cell: (r) => (
          <span className="font-medium text-stone-900">
            {formatFcfa(r.amount_fcfa)}
          </span>
        ),
      },
      {
        id: "reason",
        header: "Raison",
        cell: (r) => (
          <span className="max-w-xs truncate text-sm text-stone-600">
            {r.reason}
          </span>
        ),
      },
      {
        id: "status",
        header: "Statut",
        cell: (r) => (
          <span
            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[r.status]}`}
          >
            {STATUS_LABELS[r.status]}
          </span>
        ),
      },
      {
        id: "requested_at",
        header: "Demandé le",
        cell: (r) => (
          <span className="text-sm text-stone-500">
            {formatDate(r.requested_at)}
          </span>
        ),
      },
      {
        id: "processed_at",
        header: "Traité le",
        cell: (r) => (
          <span className="text-sm text-stone-500">
            {r.processed_at ? formatDate(r.processed_at) : "—"}
          </span>
        ),
      },
      {
        id: "processed_by",
        header: "Traité par",
        cell: (r) => (
          <span className="text-sm text-stone-600">{r.processed_by ?? "—"}</span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: (r) => (
          <div className="flex items-center gap-1">
            {r.status === "pending" && (
              <>
                <button
                  type="button"
                  onClick={() => handleApprove(r.id)}
                  title="Approuver"
                  className="rounded p-1.5 text-stone-400 hover:bg-emerald-50 hover:text-emerald-700"
                >
                  <CheckCircle className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setRejectingRefund(r)}
                  title="Rejeter"
                  className="rounded p-1.5 text-stone-400 hover:bg-red-50 hover:text-red-600"
                >
                  <XCircle className="h-3.5 w-3.5" />
                </button>
              </>
            )}
          </div>
        ),
      },
    ],
    [handleApprove],
  );

  return (
    <AdminLayout>
      <ModuleGuard moduleKey="returns-refunds">
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <RotateCcw className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Remboursements
              </h1>
              <p className="text-sm text-stone-500">
                {isLoading
                  ? "Chargement…"
                  : `${refunds.length} remboursement(s)`}
              </p>
            </div>
          </div>
          <Button onClick={() => setIsCreateOpen(true)} className="gap-1.5">
            <Plus className="h-4 w-4" />
            Nouveau remboursement
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <KPICard
            label="En attente"
            value={pendingCount}
            format="number"
            icon={<RotateCcw className="h-4 w-4" />}
          />
          <KPICard
            label="Approuvés ce mois"
            value={refunds.filter((r) => r.status === "approved").length}
            format="number"
            icon={<CheckCircle className="h-4 w-4" />}
          />
          <KPICard
            label="Rejetés"
            value={refunds.filter((r) => r.status === "rejected").length}
            format="number"
            icon={<XCircle className="h-4 w-4" />}
          />
          <KPICard
            label="Montant total remboursé"
            value={totalRefunded}
            format="currency"
            icon={<RotateCcw className="h-4 w-4" />}
          />
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as RefundStatus | "")}
            className="h-9 rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          >
            <option value="">Tous les statuts</option>
            <option value="pending">En attente</option>
            <option value="approved">Approuvé</option>
            <option value="processed">Traité</option>
            <option value="rejected">Rejeté</option>
          </select>
        </div>

        {/* Error state */}
        {isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Impossible de charger les remboursements.</span>
            <button
              type="button"
              onClick={() => refetch()}
              className="ml-auto rounded border border-red-200 bg-white px-3 py-1 text-xs text-red-700 hover:bg-red-50"
            >
              Réessayer
            </button>
          </div>
        )}

        {/* Table */}
        {!isError && (
          <>
            {!isLoading && refunds.length === 0 ? (
              <EmptyState
                icon={<RotateCcw className="h-6 w-6" />}
                title="Aucun remboursement"
                description="Les demandes de remboursement apparaîtront ici."
                action={{
                  label: "Nouveau remboursement",
                  onClick: () => setIsCreateOpen(true),
                }}
              />
            ) : (
              <AdminDataTable
                data={refunds}
                columns={columns}
                isLoading={isLoading}
              />
            )}
          </>
        )}
      </div>

      {/* Create modal */}
      <CreateRefundModal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        isSaving={createRefund.isPending}
        onSave={handleCreateSave}
      />

      {/* Reject modal */}
      <RejectModal
        open={rejectingRefund !== null}
        refund={rejectingRefund}
        onClose={() => setRejectingRefund(null)}
        onConfirm={handleRejectConfirm}
        isRejecting={rejectRefund.isPending}
      />
      </ModuleGuard>
    </AdminLayout>
  );
}
